/* eslint-disable unicorn/prevent-abbreviations */
import type { Component } from 'vue'

import type { AButtonVariantsType } from '../../components/ui/AButton/AButton.vue'
import type { ADialogProperties, AMessageDialogProperties, DialogCardFooterInterface } from '../../components/ui/dialog'
import { AConfirmDialog } from '../../components/ui/dialog'
import AFormDialog, { type FormDialogProperties } from '../../components/ui/dialog/AFormDialog.vue'
import AMessageDialog from '../../components/ui/dialog/AMessageDialog.vue'
import {
  type ComponentProperties,
  type DialogSettings,
  type MaybeWritableOrComputedRefs as MaybeWritableOrComputedReferences,
  useDialogStore,
} from './store'

export function openDialog<TComponent extends Component>(
  component: TComponent,
  attributes: MaybeWritableOrComputedReferences<ComponentProperties<TComponent>>,
  settings?: DialogSettings,
) {
  return useDialog(component, attributes, settings).open()
}

export function useDialog<TComponent extends Component>(
  component: TComponent,
  attributes: MaybeWritableOrComputedReferences<ComponentProperties<TComponent>>,
  settings?: DialogSettings,
) {
  const { open, close } = useDialogStore()

  return { close, open: () => open(component, attributes, settings) }
}

export function openMessageDialog<TComponent extends typeof AMessageDialog = typeof AMessageDialog>(
  attributes: MaybeWritableOrComputedReferences<ComponentProperties<TComponent>>,
  settings?: DialogSettings,
) {
  return openDialog(AMessageDialog, attributes, settings)
}

export function openErrorDialog<TComponent extends typeof AMessageDialog = typeof AMessageDialog>(
  attributes: MaybeWritableOrComputedReferences<Omit<ComponentProperties<TComponent>, 'type'>>,
  settings?: DialogSettings,
) {
  return openDialog(AMessageDialog, { ...attributes, type: 'error' }, settings)
}

export function openSuccessDialog<TComponent extends typeof AMessageDialog = typeof AMessageDialog>(
  attributes: MaybeWritableOrComputedReferences<Omit<ComponentProperties<TComponent>, 'type'>>,
  settings?: DialogSettings,
) {
  return openDialog(AMessageDialog, { ...attributes, type: 'success' }, settings)
}

export function useFormDialog(attributes: FormDialogProperties, settings?: DialogSettings) {
  return useDialog(AFormDialog, { persistent: true, ...attributes }, { doCloneAttributes: false, ...settings })
}

export async function useConfirmDialog<TComponent extends typeof AConfirmDialog = typeof AConfirmDialog>(
  options: {
    title: string
    description?: string
    confirm?: string
    cancel?: string
    input?: string
    variant?: AButtonVariantsType['variant']
  },
  settings?: DialogSettings,
): Promise<boolean> {
  return new Promise((resolve) => {
    return openDialog(
      AConfirmDialog,
      {
        ...options,
        confirm: {
          title: options.confirm,
          click: () => resolve(true),
          variant: options.variant,
        } satisfies DialogCardFooterInterface,
        cancel: { title: options.cancel, click: () => resolve(false) } satisfies DialogCardFooterInterface,
      },
      settings,
    )
  })
}
